import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { tokens } from '../../theme/tokens';

export interface ResponsiveRowsColumn {
  /** Passed back to `renderCell` to pick the cell. */
  key: string;
  /** Column heading on `md+`; also the small mobile label when `mobileLabel` is set. */
  header: React.ReactNode;
  /** Desktop grid-template fragment for this column, e.g. `'120px'` or `'minmax(0,1.5fr)'`. */
  width: string;
  /** Below `md`, show `header` as a small uppercase label above the cell (for cells that are not self-describing). */
  mobileLabel?: boolean;
  /** `'end'` right-aligns inline content on `md+` and left-aligns it below `md`. */
  align?: 'start' | 'end';
}

interface ResponsiveRowsProps<T> {
  columns: ResponsiveRowsColumn[];
  rows: T[];
  getRowKey: (row: T) => string;
  renderCell: (row: T, columnKey: string) => React.ReactNode;
  /**
   * Extra styles for one row's grid box (e.g. an active-row background). Merged
   * in after the row's own `padding`/`gap`/border, so it can also override
   * just `px`/`py` (or the divider's `border...` side) while leaving the rest.
   */
  rowSx?: (row: T) => SxProps<Theme>;
  /** Renders `skeletonRows` placeholder rows in place of `rows`. */
  isLoading?: boolean;
  skeletonRows?: number;
  /** Shown, inside the row padding, when not loading and `rows` is empty. */
  emptyState?: React.ReactNode;
  /** Hide the `md+` header row (for a card that groups rows under its own headings). */
  showHeader?: boolean;
  /** Gap between cells. On `md+` this is the column gap; below `md` the gap between stacked cells. */
  gap?: string | { xs: string; md: string };
  /** Padding of each row. */
  padding?: string;
  /** Padding of the header row; keep its horizontal inset equal to `padding`'s. */
  headerPadding?: string;
  /** Extra styles merged onto the header row's container box, e.g. `{ bgcolor: tokens.surface.subtle }`. */
  headerSx?: SxProps<Theme>;
  /** Extra styles merged onto each header cell's label, for a page whose type scale differs from the default (font size/weight, letter-spacing, colour). */
  headerCellSx?: SxProps<Theme>;
  /**
   * Which hairline each row (and loading skeleton) draws: `'top'` (default,
   * the original behaviour — a divider between rows, doubled against the
   * header's own bottom border) or `'bottom'` (a divider below each row,
   * including the last). `'none'` draws no built-in divider, so `rowSx` can
   * supply its own border colour/side entirely.
   */
  rowDivider?: 'top' | 'bottom' | 'none';
}

const mobileCellLabelSx = {
  display: { xs: 'block', md: 'none' },
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  color: tokens.ink.faint,
  mb: '4px',
} as const;

const headerLabelSx = {
  fontSize: 11.5,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  color: tokens.ink.faint,
} as const;

/**
 * A CSS-grid "table" that stacks below `md`: on desktop a faint uppercase
 * header row and one grid row per item (`alignItems: center`); on phones and
 * portrait tablets the header is hidden, each row becomes a single column,
 * and cells that opt in carry their header as a small label. This is the
 * Admin → Plans pattern (`docs/internal/MOBILE_UI_ASSESSMENT.md` Phase 0) made
 * reusable so fixed-`px` grids do not re-derive the breakpoint object.
 *
 * `headerSx` and `headerCellSx` let a page match its own header background
 * and type scale (e.g. the `bv*` visual system); `rowDivider` picks which
 * side each row's hairline sits on, and `rowSx` can still override its
 * colour, or `px`/`py` alone, on top of that.
 */
export function ResponsiveRows<T>({
  columns,
  rows,
  getRowKey,
  renderCell,
  rowSx,
  isLoading = false,
  skeletonRows = 4,
  emptyState,
  showHeader = true,
  gap = { xs: '12px', md: '16px' },
  padding = '16px 20px',
  headerPadding = '10px 20px',
  headerSx,
  headerCellSx,
  rowDivider = 'top',
}: ResponsiveRowsProps<T>) {
  const desktopTemplate = columns.map(column => column.width).join(' ');
  const gridTemplateColumns = { xs: 'minmax(0,1fr)', md: desktopTemplate };
  const desktopGap = typeof gap === 'string' ? gap : gap.md;
  const hairline = `1px solid ${tokens.line.hairline}`;
  const dividerSx = rowDivider === 'none' ? {} : rowDivider === 'bottom' ? { borderBottom: hairline } : { borderTop: hairline };
  const headerSxList = Array.isArray(headerSx) ? headerSx : headerSx ? [headerSx] : [];
  const headerCellSxList = Array.isArray(headerCellSx) ? headerCellSx : headerCellSx ? [headerCellSx] : [];

  return (
    <>
      {showHeader && (
        <Box
          sx={[
            {
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns,
              columnGap: desktopGap,
              p: headerPadding,
              borderBottom: hairline,
            },
            ...headerSxList,
          ]}
        >
          {columns.map(column => (
            <Typography
              key={column.key}
              sx={[{ ...headerLabelSx, textAlign: column.align === 'end' ? 'right' : undefined }, ...headerCellSxList]}
            >
              {column.header}
            </Typography>
          ))}
        </Box>
      )}

      {isLoading &&
        Array.from({ length: skeletonRows }).map((_, i) => (
          <Box key={i} sx={{ p: padding, ...dividerSx }}>
            <Skeleton height={36} />
          </Box>
        ))}

      {!isLoading && rows.length === 0 && emptyState !== undefined && <Box sx={{ p: padding }}>{emptyState}</Box>}

      {!isLoading &&
        rows.map(row => {
          const extra = rowSx?.(row);
          const extraSx = Array.isArray(extra) ? extra : extra ? [extra] : [];
          return (
            <Box
              key={getRowKey(row)}
              sx={[
                {
                  display: 'grid',
                  gridTemplateColumns,
                  gap,
                  p: padding,
                  ...dividerSx,
                  alignItems: { xs: 'stretch', md: 'center' },
                },
                ...extraSx,
              ]}
            >
              {columns.map(column => (
                <Box
                  key={column.key}
                  sx={{ minWidth: 0, textAlign: column.align === 'end' ? { xs: 'left', md: 'right' } : undefined }}
                >
                  {column.mobileLabel && <Typography sx={mobileCellLabelSx}>{column.header}</Typography>}
                  {renderCell(row, column.key)}
                </Box>
              ))}
            </Box>
          );
        })}
    </>
  );
}
